import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getShipmentJobs } from '@wove/api'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Badge,
  Spinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  cn,
} from '@wove/web-ui'
import { Clock, CheckCircle, XCircle, AlertCircle, Shield, GitMerge, ChevronDown, ChevronRight } from 'lucide-react'
import { format } from 'date-fns'
import { CrossValidationResults } from './CrossValidationResults'

interface JobHistoryDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  shipmentId: string
}

interface Job {
  id: string
  type: string
  status: string
  progress: number
  documentIds: string[]
  result?: any
  createdAt: string
  startedAt?: string
  completedAt?: string
}

const statusConfig = {
  pending: {
    icon: Clock,
    color: 'text-gray-500',
    badgeVariant: 'secondary' as const,
  },
  processing: {
    icon: Clock,
    color: 'text-blue-500',
    badgeVariant: 'default' as const,
  },
  completed: {
    icon: CheckCircle,
    color: 'text-green-500',
    badgeVariant: 'success' as const,
  },
  failed: {
    icon: XCircle,
    color: 'text-red-500',
    badgeVariant: 'destructive' as const,
  },
}

export const JobHistoryDialog: React.FC<JobHistoryDialogProps> = ({
  open,
  onOpenChange,
  shipmentId,
}) => {
  const [expandedJobs, setExpandedJobs] = useState<Set<string>>(new Set())

  const { data, isLoading, error } = useQuery({
    queryKey: ['shipment', shipmentId, 'jobs'],
    queryFn: () => getShipmentJobs(shipmentId),
    enabled: open,
  })

  const toggleJobExpanded = (job: Job) => {
    const jobKey = `${job.type}-${job.id}`
    const isExpanded = expandedJobs.has(jobKey)
    
    if (isExpanded) {
      setExpandedJobs(prev => {
        const next = new Set(prev)
        next.delete(jobKey)
        return next
      })
    } else {
      setExpandedJobs(prev => new Set(prev).add(jobKey))
    }
  }

  const renderJobResults = (job: Job) => {
    const result = job.result

    if (!result) return null

    if (job.type === 'cross-validation') {
      return (
        <div className="mt-4 border-t pt-4">
          <CrossValidationResults results={result} />
        </div>
      )
    } else {
      // Merge results
      console.log('Merge result structure:', result)
      
      // Handle different possible structures
      let mergedFields = {}
      let conflicts = []
      
      if (result.mergedDocument) {
        mergedFields = result.mergedDocument
        conflicts = result.conflicts || []
      } else if (result.mergedFields) {
        mergedFields = result.mergedFields
        conflicts = result.conflicts || []
      } else if (typeof result === 'object' && !Array.isArray(result)) {
        // The result itself might be the merged document
        mergedFields = result
      }

      // Count actual fields
      const fieldCount = Object.keys(mergedFields).length

      return (
        <div className="mt-4 space-y-3 border-t pt-4">
          <div className="text-xs text-muted-foreground">
            Merged {fieldCount} fields from {job.documentIds.length} documents
          </div>
          
          {conflicts.length > 0 && (
            <div>
              <h5 className="text-xs font-medium mb-2 flex items-center gap-1">
                <AlertCircle className="h-3 w-3 text-amber-500" />
                Conflicts Resolved ({conflicts.length})
              </h5>
              <div className="space-y-1">
                {conflicts.slice(0, 3).map((conflict: any, idx: number) => (
                  <div key={idx} className="text-xs bg-amber-50 rounded p-2">
                    <strong>{conflict.field || conflict.fieldName}:</strong> Used value from {conflict.source || conflict.selectedDocument || 'primary document'}
                  </div>
                ))}
                {conflicts.length > 3 && (
                  <div className="text-xs text-muted-foreground pl-4">
                    ... and {conflicts.length - 3} more conflicts
                  </div>
                )}
              </div>
            </div>
          )}

          {fieldCount === 0 && conflicts.length === 0 && (
            <div className="text-xs text-muted-foreground">
              No merge results found. The documents may not have extractable fields.
            </div>
          )}
        </div>
      )
    }
  }

  const renderJobItem = (job: Job) => {
    const config = statusConfig[job.status as keyof typeof statusConfig] || statusConfig.pending
    const Icon = config.icon
    const jobKey = `${job.type}-${job.id}`
    const isExpanded = expandedJobs.has(jobKey)
    const hasResults = job.status === 'completed'

    return (
      <div key={job.id} className="border rounded-lg overflow-hidden">
        <div
          className={cn(
            "p-4 space-y-2",
            hasResults && "cursor-pointer hover:bg-gray-50"
          )}
          onClick={hasResults ? () => toggleJobExpanded(job) : undefined}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              {hasResults && (
                <div className="mt-1">
                  {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </div>
              )}
              <Icon className={cn('h-5 w-5 mt-0.5', config.color)} />
              <div>
                <div className="flex items-center gap-2">
                  <h4 className="text-sm font-medium">
                    {job.type === 'cross-validation' ? 'Cross-Validation' : 'Document Merge'}
                  </h4>
                  <Badge variant={config.badgeVariant}>{job.status}</Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {job.documentIds.length} documents
                </p>
              </div>
            </div>
            <div className="text-right text-xs text-muted-foreground">
              <p>{format(new Date(job.createdAt), 'MMM d, yyyy')}</p>
              <p>{format(new Date(job.createdAt), 'h:mm a')}</p>
            </div>
          </div>

          {job.status === 'processing' && (
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${job.progress}%` }}
              />
            </div>
          )}

          {job.completedAt && (
            <p className="text-xs text-muted-foreground">
              Duration: {getDuration(job.createdAt, job.completedAt)}
            </p>
          )}
        </div>

        {isExpanded && hasResults && (
          <div className="px-4 pb-4">
            {renderJobResults(job)}
          </div>
        )}
      </div>
    )
  }

  const getDuration = (start: string, end: string) => {
    const startDate = new Date(start)
    const endDate = new Date(end)
    const durationMs = endDate.getTime() - startDate.getTime()
    const seconds = Math.floor(durationMs / 1000)
    const minutes = Math.floor(seconds / 60)
    
    if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`
    }
    return `${seconds}s`
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Job History
          </DialogTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Click on completed jobs to view their results
          </p>
        </DialogHeader>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : error ? (
          <div className="text-center py-8 text-red-500">
            <AlertCircle className="h-8 w-8 mx-auto mb-2" />
            <p>Failed to load job history</p>
          </div>
        ) : data?.success ? (
          <Tabs defaultValue="all" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="all">
                All ({(data.jobs.crossValidation?.length || 0) + (data.jobs.merge?.length || 0)})
              </TabsTrigger>
              <TabsTrigger value="cross-validation" className="flex items-center gap-1">
                <Shield className="h-4 w-4" />
                Cross-Validation ({data.jobs.crossValidation?.length || 0})
              </TabsTrigger>
              <TabsTrigger value="merge" className="flex items-center gap-1">
                <GitMerge className="h-4 w-4" />
                Merge ({data.jobs.merge?.length || 0})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="all" className="space-y-3 max-h-[50vh] overflow-y-auto">
              {[...(data.jobs.crossValidation || []), ...(data.jobs.merge || [])]
                .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                .map(renderJobItem)}
              {(data.jobs.crossValidation?.length || 0) + (data.jobs.merge?.length || 0) === 0 && (
                <p className="text-center text-muted-foreground py-8">No jobs found</p>
              )}
            </TabsContent>

            <TabsContent value="cross-validation" className="space-y-3 max-h-[50vh] overflow-y-auto">
              {data.jobs.crossValidation?.map(renderJobItem)}
              {!data.jobs.crossValidation?.length && (
                <p className="text-center text-muted-foreground py-8">
                  No cross-validation jobs found
                </p>
              )}
            </TabsContent>

            <TabsContent value="merge" className="space-y-3 max-h-[50vh] overflow-y-auto">
              {data.jobs.merge?.map(renderJobItem)}
              {!data.jobs.merge?.length && (
                <p className="text-center text-muted-foreground py-8">No merge jobs found</p>
              )}
            </TabsContent>
          </Tabs>
        ) : (
          <p className="text-center text-muted-foreground py-8">No job history available</p>
        )}
      </DialogContent>
    </Dialog>
  )
}